import React, { useMemo, useState } from "react";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  BarChart,
  Tooltip,
  Label,
} from "recharts";
import PropTypes from "prop-types";
import getStyled from "../../../hooks/getStyled";

const { theme } = getStyled();

function groupBy(objectArray, property, defaultValue) {
  return objectArray.reduce((acc, obj) => {
    let key = obj[property];
    if (key === undefined) key = defaultValue;

    if (!acc[key]) {
      acc[key] = [];
    }
    // Add object to list for given key's value
    acc[key].push(obj);
    return acc;
  }, {});
}

const SFHorizontalBarChartStacked = ({
  width,
  height,
  yAxisKeyName,
  dataKeys,
  data,
  showLegend,
  xAxisTickFormatter,
  yAxisTickFormatter,
  tooltip,
  barSize,
  barGap,
  graphColors = {
    a: [
      { fill: theme.palette.blue100.main, color: theme.palette.black.main },
      { fill: theme.palette.blue200.main, color: theme.palette.white.main },
      { fill: theme.palette.blue300.main, color: theme.palette.white.main },
      { fill: theme.palette.blue500.main, color: theme.palette.white.main },
    ],
    b: [
      { fill: theme.palette.grey250.main, color: theme.palette.black.main },
      { fill: theme.palette.grey250.main, color: theme.palette.black.main },
      { fill: theme.palette.grey250.main, color: theme.palette.black.main },
      { fill: theme.palette.grey250.main, color: theme.palette.black.main },
    ],
  },
  insideLabel,
  xLabel,
  xLabelPosition,
  xTickCount,
  title = null,
  margin,
}) => {
  const [selectedLabel, setSelectedLabel] = useState(null);
  const dataKeyGroups = groupBy(dataKeys, "stackId", "a");

  const MoonSvg = (props, dataKey) => {
    // Find the data point of the bar and the index of this dataKey in the bar's keys
    const bar = data.find((e) => e.name === props.name);
    const index = bar.keys?.indexOf(dataKey);

    const CustomSvg = ({ x, y, fill }) => (
      <svg
        x={x}
        y={y}
        width="25"
        height="22"
        viewBox="0 0 9 12"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3 12C6.31371 12 9 9.31371 9 6C9 2.68629 6.31371 -2.34843e-07 3 -5.24537e-07L1.04907e-06 -7.86805e-07C3.31371 -4.97111e-07 6 2.68629 6 6C6 9.31371 3.31371 12 -9.53674e-07 12L3 12Z"
          fill={fill}
        />
      </svg>
    );

    if (bar) {
      return (
        <>
          {/* If this is not the first bar, add an svg at the beginning of the bar */}
          {/* so that it appears rounded off */}
          {index > 0 && (
            <CustomSvg x={props.x - 16} y={props.y - 1} fill={props.fill} />
          )}
          {/* If this is the last bar add another svg at the end */}
          {/* This covers the case of the width of the bar being too small and apeearing rectangular */}
          {/* If this bar is hovered make the svg background color match the hover color of the bar */}
          {index === bar.keys.length - 1 && (
            <CustomSvg
              x={props.x - 16 + props.width}
              y={props.y - 1}
              fill={
                props.name === selectedLabel
                  ? theme.palette.grey200.main
                  : props.fill
              }
            />
          )}
        </>
      );
    }
    return null;
  };

  const domain = useMemo(() => {
    // This function takes a data point and calculates the total of all datakeys
    // for each stack and returns the total maximum of the stacks
    const getTotal = (x) => {
      const stackTotals = dataKeys.reduce(
        (acc, cur) => ({
          ...acc,
          [cur.stackId || "a"]:
            (acc[cur.stackId || "a"] ?? 0) + (x[cur.name] ?? 0),
        }),
        {}
      );

      return Math.max(...Object.keys(stackTotals).map((x) => stackTotals[x]));
    };

    // Calculate the maximum value from the data and round it up with the formula
    // Maximum value + 5 rounded up to the nearest 10
    const maximumValue = Math.max(...data.map((x) => getTotal(x)));
    const rounded = Math.ceil((maximumValue + 5) / 10) * 10;

    return [0, rounded];
  });

  const setRelatedJobRoles = (props, index, stackId) => {
    const { x, y, value, width } = props;
    if (width > 25) {
      return (
        <g>
          <text
            x={x + width - 25}
            y={y + barSize - 7}
            fontSize={10}
            fill={getColor(stackId, index, graphColors).color}
          >
            {value}
          </text>
        </g>
      );
    }
    return null;
  };

  const getColor = (stackId, index, graphColors) => {
    return graphColors[stackId] && graphColors[stackId][index]
      ? graphColors[stackId][index]
      : graphColors[index];
  };

  const CustomBar = (props) => {
    const { fill, x, y, width, height } = props;
    const bar = data.find((e) => e.name === props.name);
    const index = bar.keys?.indexOf(props.dataKey);
    // If there is no index or it is the first bar, make it round
    let radius = !index || index === 0 ? height / 2 : 3;

    return (
      <rect
        width={width}
        height={height}
        x={x}
        y={y}
        stroke="none"
        fill={fill}
        rx={radius}
      />
    );
  };

  return (
    <ResponsiveContainer width={width} height={height}>
      <BarChart
        title={title}
        layout="vertical"
        data={data}
        margin={margin || { bottom: 30, top: 5, left: 0, right: 0 }}
        barSize={barSize}
        barGap={barGap}
        // Track which data point is currently hovered to apply specific styling
        onMouseLeave={() => setSelectedLabel(null)}
        onMouseMove={(props) => {
          if (props.activeLabel !== selectedLabel) {
            setSelectedLabel(props.activeLabel);
          }
        }}
      >
        <CartesianGrid
          strokeDasharray="3 1"
          horizontal={false}
          stroke={theme.palette.grey200.main}
        />
        <XAxis
          type="number"
          axisLine={false}
          height={12}
          tickFormatter={xAxisTickFormatter}
          fontSize={10}
          tickCount={xTickCount}
          tickLine={false}
          domain={domain}
        >
          {xLabel && (
            <Label value={xLabel} position={xLabelPosition} offset={15} />
          )}
        </XAxis>
        <YAxis
          type="category"
          dataKey={yAxisKeyName}
          axisLine={false}
          tickLine={false}
          fontSize={12}
          width={150}
          tickFormatter={yAxisTickFormatter}
        />
        {tooltip && (
          <Tooltip
            content={tooltip}
            cursor={{ fill: theme.palette.grey200.main }}
          />
        )}
        {showLegend && <Legend />}
        {Object.keys(dataKeyGroups).map((stackId, index1) =>
          dataKeyGroups[stackId].map((item, index2) => (
            <>
              <Bar
                key={item.name}
                stackId={item.stackId || "a"}
                dataKey={item.name}
                fill={getColor(stackId, index2, graphColors).fill}
                //barSize={barSize}
                //minPointSize={10}
                shape={(props) => <CustomBar {...props} dataKey={item.name} />}
              >
                {insideLabel > 0 && (
                  <LabelList
                    dataKey={item.name}
                    // position="insideRight"
                    // fill={graphColors[index2].color}
                    fill={getColor(stackId, index2, graphColors).color}
                    //fontSize={10}
                    content={(props) =>
                      setRelatedJobRoles(props, index2, stackId)
                    }
                  />
                )}
                {index2 > 0 && (
                  <LabelList
                    dataKey={item.name}
                    position="insideRight"
                    fill="#FFFFFF"
                    content={(props) => MoonSvg(props, item.name)}
                    fontSize={barSize}
                  //offset={20}
                  />
                )}
              </Bar>
            </>
          ))
        )}
      </BarChart>
    </ResponsiveContainer>
  );
};
SFHorizontalBarChartStacked.defaultProps = {
  width: "100%",
  height: "100%",
  yAxisKeyName: "name",
  showLegend: false,
  xAxisTickFormatter: null,
  yAxisTickFormatter: null,
  tooltip: null,
  barSize: 10,
  barGap: null,
  insideLabel: false,
  xLabelPosition: "bottom",
  xTickCount: 5,
  margin: { bottom: 30, top: 5, left: 0, right: 0 },
};

SFHorizontalBarChartStacked.propTypes = {
  width: PropTypes.string,
  height: PropTypes.any,
  yAxisKeyName: PropTypes.string,
  dataKeys: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      stackId: PropTypes.string,
    })
  ),
  data: PropTypes.arrayOf(PropTypes.object),
  showLegend: PropTypes.bool,
  xAxisTickFormatter: PropTypes.func,
  yAxisTickFormatter: PropTypes.func,
  tooltip: PropTypes.func,
  barSize: PropTypes.number,
  barGap: PropTypes.number,
  graphColors: PropTypes.arrayOf(
    PropTypes.shape({
      fill: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  insideLabel: PropTypes.bool,
  xLabel: PropTypes.any,
  xLabelPosition: PropTypes.string,
  xTickCount: PropTypes.number,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
};
export default SFHorizontalBarChartStacked;
